import { Controller } from "@hotwired/stimulus"

// Field Mappings Import Controller
// Handles file upload, validation, and preview for field mappings import
export default class extends Controller {
  static targets = [
    "dropZone",
    "fileInput",
    "uploadContent",
    "selectedContent",
    "fileName",
    "fileSize",
    "clearBtn",
    "formatDetection",
    "detectedFormat",
    "previewSection",
    "previewContent",
    "previewBtn",
    "submitBtn",
    "buttonText",
    "progress",
    "progressBar",
    "progressText",
    "validationResults",
    "validationContent",
    "messages",
    "successMessage",
    "errorMessage",
    "successText",
    "errorText"
  ]

  connect() {
    console.log("Field mappings import controller connected")
    this.selectedFile = null
    this.disableButtons()

    // Find the modal element (parent of this controller)
    this.modalElement = this.element.closest("#field-mappings-import-modal")

    // Set up click outside to close
    if (this.modalElement) {
      this.modalElement.addEventListener("click", (e) => {
        if (e.target === this.modalElement) {
          this.closeModal()
        }
      })
    }
  }

  // Close the modal
  closeModal(event) {
    if (event) event.preventDefault()
    if (this.modalElement) {
      this.modalElement.classList.add("hidden")
      document.body.style.overflow = ""
      this.resetModal()
    }
  }

  // Reset modal state
  resetModal() {
    this.clearFile()
    this.clearMessages()

    // Reset form
    const form = this.element.querySelector("form")
    if (form) {
      form.reset()
    }
  }

  // Trigger file input when clicking drop zone
  triggerFileInput(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  // Handle file selection from input
  handleFileSelect(event) {
    const file = event.target.files[0]
    if (file) {
      this.processFile(file)
    }
  }

  // Handle drag over
  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add("border-blue-400", "bg-blue-900/20")
    }
  }

  // Handle drag leave
  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("border-blue-400", "bg-blue-900/20")
    }
  }

  // Handle file drop
  handleFileDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("border-blue-400", "bg-blue-900/20")
    }
    const file = event.dataTransfer.files[0]
    if (file) {
      this.processFile(file)
    }
  }

  // Process the selected file
  processFile(file) {
    // Validate file type
    const allowedExtensions = [".json", ".csv", ".yaml", ".yml"]
    const fileExt = "." + file.name.split(".").pop().toLowerCase()

    if (!allowedExtensions.includes(fileExt)) {
      this.showError("Please select a JSON, CSV, or YAML file.")
      return
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      this.showError("File size must be less than 10MB")
      return
    }

    this.selectedFile = file

    // Assign file to the form input
    if (this.hasFileInputTarget) {
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      this.fileInputTarget.files = dataTransfer.files
    }

    // Update UI
    this.updateFileDisplay(file)
    this.enableButtons()

    // Auto-preview if enabled
    const autoPreviewCheckbox = this.element.querySelector('input[name*="auto_preview"]')
    if (autoPreviewCheckbox && autoPreviewCheckbox.checked) {
      setTimeout(() => this.previewFile(), 100)
    }

    this.clearMessages()
  }

  // Update file display
  updateFileDisplay(file) {
    if (this.hasFileNameTarget) {
      this.fileNameTarget.textContent = file.name
    }
    if (this.hasFileSizeTarget) {
      this.fileSizeTarget.textContent = this.formatFileSize(file.size)
    }
    if (this.hasUploadContentTarget) {
      this.uploadContentTarget.classList.add("hidden")
    }
    if (this.hasSelectedContentTarget) {
      this.selectedContentTarget.classList.remove("hidden")
    }

    // Show format detection
    const format = this.detectFileFormat(file)
    if (this.hasDetectedFormatTarget) {
      this.detectedFormatTarget.textContent = format.toUpperCase()
    }
    if (this.hasFormatDetectionTarget) {
      this.formatDetectionTarget.classList.remove("hidden")
    }
  }

  // Clear file selection
  clearFile(event) {
    if (event) event.preventDefault()

    this.selectedFile = null

    if (this.hasFileInputTarget) {
      this.fileInputTarget.value = ""
    }
    if (this.hasUploadContentTarget) {
      this.uploadContentTarget.classList.remove("hidden")
    }
    if (this.hasSelectedContentTarget) {
      this.selectedContentTarget.classList.add("hidden")
    }
    if (this.hasFormatDetectionTarget) {
      this.formatDetectionTarget.classList.add("hidden")
    }
    if (this.hasPreviewSectionTarget) {
      this.previewSectionTarget.classList.add("hidden")
    }

    this.disableButtons()
    this.clearMessages()
  }

  // Preview file contents
  async previewFile(event) {
    if (event) event.preventDefault()

    if (!this.selectedFile) {
      this.showError("Please select a file first")
      return
    }

    try {
      const text = await this.selectedFile.text()
      const format = this.detectFileFormat(this.selectedFile)

      let previewText = text
      if (format === "json") {
        try {
          const parsed = JSON.parse(text)
          previewText = JSON.stringify(parsed, null, 2)
        } catch (e) {
          this.showError("Invalid JSON format")
          return
        }
      }

      // Limit preview length
      if (previewText.length > 2000) {
        previewText = previewText.substring(0, 2000) + "\n\n... (truncated for preview)"
      }

      if (this.hasPreviewContentTarget) {
        this.previewContentTarget.textContent = previewText
      }
      if (this.hasPreviewSectionTarget) {
        this.previewSectionTarget.classList.remove("hidden")
      }

      // Auto-validate if enabled
      const validateCheckbox = this.element.querySelector('input[name*="validate_before_import"]')
      if (validateCheckbox && validateCheckbox.checked) {
        this.validateFileContent(text, format)
      }
    } catch (error) {
      this.showError("Failed to read file: " + error.message)
    }
  }

  // Validate file content
  validateFileContent(content, format) {
    const warnings = []
    let isValid = true

    try {
      if (format === "json") {
        const data = JSON.parse(content)

        if (Array.isArray(data)) {
          data.forEach((item, index) => {
            if (!item.application_field) {
              warnings.push(`Row ${index + 1}: Missing application_field`)
              isValid = false
            }
            if (!item.salesforce_field) {
              warnings.push(`Row ${index + 1}: Missing salesforce_field`)
              isValid = false
            }
          })
        } else if (data.mappings && Array.isArray(data.mappings)) {
          data.mappings.forEach((item, index) => {
            if (!item.application_field) {
              warnings.push(`Mapping ${index + 1}: Missing application_field`)
              isValid = false
            }
            if (!item.salesforce_field) {
              warnings.push(`Mapping ${index + 1}: Missing salesforce_field`)
              isValid = false
            }
          })
        } else {
          warnings.push("Expected array of mappings or object with mappings property")
          isValid = false
        }
      } else if (format === "csv") {
        const lines = content.split("\n").filter((line) => line.trim())
        if (lines.length < 2) {
          warnings.push("CSV file must have at least a header row and one data row")
          isValid = false
        } else {
          const headers = lines[0].split(",").map((h) => h.trim().toLowerCase())
          if (!headers.includes("application_field") && !headers.includes("application field")) {
            warnings.push('CSV must have an "application_field" column')
            isValid = false
          }
          if (!headers.includes("salesforce_field") && !headers.includes("salesforce field")) {
            warnings.push('CSV must have a "salesforce_field" column')
            isValid = false
          }
        }
      }

      if (warnings.length > 0) {
        this.showValidationResults(warnings, isValid)
      } else {
        this.showValidationResults(["File format appears valid"], true)
      }
    } catch (error) {
      this.showValidationResults(["Failed to validate file: " + error.message], false)
    }
  }

  // Show validation results
  showValidationResults(messages, isValid) {
    const resultClass = isValid ? "text-green-300" : "text-red-300"
    const bgClass = isValid ? "bg-green-900/20 border-green-900/30" : "bg-red-900/20 border-red-900/30"

    if (this.hasValidationContentTarget) {
      this.validationContentTarget.innerHTML = `
        <div class="${bgClass} rounded p-3">
          ${messages.map((msg) => `<div class="${resultClass} text-sm">${msg}</div>`).join("")}
        </div>
      `
    }
    if (this.hasValidationResultsTarget) {
      this.validationResultsTarget.classList.remove("hidden")
    }
  }

  // Handle form submission
  handleSubmit(event) {
    if (!this.selectedFile) {
      event.preventDefault()
      this.showError("Please select a file to import")
      return
    }

    // Show progress
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove("hidden")
    }
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
    }
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = "Importing..."
    }

    // Simulate progress
    setTimeout(() => this.updateProgress(30, "Validating file..."), 500)
    setTimeout(() => this.updateProgress(60, "Processing mappings..."), 1000)
    setTimeout(() => this.updateProgress(90, "Saving to database..."), 1500)
  }

  // Utility methods
  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  detectFileFormat(file) {
    if (file.type === "application/json" || file.name.toLowerCase().endsWith(".json")) {
      return "json"
    } else if (file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv")) {
      return "csv"
    } else if (file.type.includes("yaml") || file.name.toLowerCase().match(/\.(yaml|yml)$/)) {
      return "yaml"
    }
    return "unknown"
  }

  enableButtons() {
    if (this.hasPreviewBtnTarget) {
      this.previewBtnTarget.disabled = false
      this.previewBtnTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = false
      this.submitBtnTarget.classList.remove("opacity-50", "cursor-not-allowed")
    }
  }

  disableButtons() {
    if (this.hasPreviewBtnTarget) {
      this.previewBtnTarget.disabled = true
      this.previewBtnTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
    if (this.hasSubmitBtnTarget) {
      this.submitBtnTarget.disabled = true
      this.submitBtnTarget.classList.add("opacity-50", "cursor-not-allowed")
    }
  }

  updateProgress(percent, text) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = percent + "%"
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = text
    }
  }

  showError(message) {
    this.clearMessages()
    if (this.hasErrorTextTarget) {
      this.errorTextTarget.textContent = message
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.remove("hidden")
    }
    if (this.hasMessagesTarget) {
      this.messagesTarget.classList.remove("hidden")
    }
  }

  showSuccess(message) {
    this.clearMessages()
    if (this.hasSuccessTextTarget) {
      this.successTextTarget.textContent = message
    }
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.remove("hidden")
    }
    if (this.hasMessagesTarget) {
      this.messagesTarget.classList.remove("hidden")
    }
  }

  clearMessages() {
    if (this.hasMessagesTarget) {
      this.messagesTarget.classList.add("hidden")
    }
    if (this.hasSuccessMessageTarget) {
      this.successMessageTarget.classList.add("hidden")
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden")
    }
    if (this.hasValidationResultsTarget) {
      this.validationResultsTarget.classList.add("hidden")
    }
  }
}
